import { useCallback } from 'react'

import {
  bulkUploadDrugPrice,
  deleteDrugPrice,
  getDrugDetail,
  getDrugPriceThreshold,
  getOrgDrugCommunityPrices,
  getOrgDrugConfigs,
  saveDrugPrice,
  saveDrugPriceThreshold,
  searchNadacDrugs,
  updateDrugPrice,
} from 'features/organization/api'
import {
  BulkUploadDrugPriceParams,
  BulkUploadDrugPriceResponse,
  DrugDetailResponse,
  DrugPriceListRequest,
  DrugPriceListResponse,
  DrugPriceSaveRequest,
  DrugPriceStringResponse,
  DrugPriceThresholdResponse,
  DrugPriceUpdateRequest,
  NadacDrugSearchRequest,
  NadacDrugSearchResponse,
  OrgDrugCommunityPriceRequest,
  OrgDrugCommunityPriceResponse,
  SaveThresholdRequest,
} from 'features/organization/types/drugPriceConfigTypes'

export type DrugPriceConfigServiceOperators = {
  getOrgDrugConfigs: (data: DrugPriceListRequest) => Promise<DrugPriceListResponse>
  saveDrugPrice: (data: DrugPriceSaveRequest) => Promise<DrugPriceStringResponse>
  updateDrugPrice: (data: DrugPriceUpdateRequest) => Promise<DrugPriceStringResponse>
  deleteDrugPrice: (id: number, level: number) => Promise<DrugPriceStringResponse>
  bulkUploadDrugPrice: (data: BulkUploadDrugPriceParams) => Promise<BulkUploadDrugPriceResponse>
  getOrgDrugCommunityPrices: (
    data: OrgDrugCommunityPriceRequest,
  ) => Promise<OrgDrugCommunityPriceResponse>
  getDrugPriceThreshold: (organizationId: number) => Promise<DrugPriceThresholdResponse>
  saveDrugPriceThreshold: (payload: SaveThresholdRequest) => Promise<DrugPriceStringResponse>
  searchNadacDrugs: (data: NadacDrugSearchRequest) => Promise<NadacDrugSearchResponse>
  getDrugDetail: (organizationId: number, ndc: string) => Promise<DrugDetailResponse>
}

/**
 * Service hook for the organization Drug Price Configuration module. Wraps the
 * DrugPriceConfig API calls as memoised callbacks, following the App Config
 * peer convention (component-local state + direct API calls, no slice/saga).
 */
export const useDrugPriceConfigService = (): Readonly<DrugPriceConfigServiceOperators> => ({
  getOrgDrugConfigs: useCallback((data: DrugPriceListRequest) => getOrgDrugConfigs(data), []),
  saveDrugPrice: useCallback((data: DrugPriceSaveRequest) => saveDrugPrice(data), []),
  updateDrugPrice: useCallback((data: DrugPriceUpdateRequest) => updateDrugPrice(data), []),
  deleteDrugPrice: useCallback((id: number, level: number) => deleteDrugPrice(id, level), []),
  bulkUploadDrugPrice: useCallback(
    (data: BulkUploadDrugPriceParams) => bulkUploadDrugPrice(data),
    [],
  ),
  getOrgDrugCommunityPrices: useCallback(
    (data: OrgDrugCommunityPriceRequest) => getOrgDrugCommunityPrices(data),
    [],
  ),
  getDrugPriceThreshold: useCallback(
    (organizationId: number) => getDrugPriceThreshold(organizationId),
    [],
  ),
  saveDrugPriceThreshold: useCallback(
    (payload: SaveThresholdRequest) => saveDrugPriceThreshold(payload),
    [],
  ),
  searchNadacDrugs: useCallback((data: NadacDrugSearchRequest) => searchNadacDrugs(data), []),
  getDrugDetail: useCallback((organizationId: number, ndc: string) => getDrugDetail(organizationId, ndc), []),
})

export default useDrugPriceConfigService
